import { motion, useReducedMotion } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { scrollToId, trackEvent } from "../lib/tracking";
import { ANDREA_PHONE, CALENDLY_URL, contactClick } from "../lib/contact";
import { Thread } from "./Thread";

const HERO_IMG =
  "https://images.pexels.com/photos/18649620/pexels-photo-18649620.jpeg?auto=compress&cs=tinysrgb&w=1600";
const DETAIL_IMG =
  "https://images.unsplash.com/photo-1555252333-9f8e92e65df9?q=80&w=600&auto=format&fit=crop";

const ease = [0.22, 1, 0.36, 1];

export const Hero = () => {
  const reduce = useReducedMotion();
  const rise = (delay) => ({
    initial: reduce ? false : { opacity: 0, y: 24 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.9, delay, ease },
  });

  return (
    <section id="top" data-testid="hero-section" className="relative bg-ivory overflow-hidden">
      {/* Documentary image — bleeds to the right edge of the canvas on desktop */}
      <div aria-hidden="true" className="hidden lg:block absolute top-0 bottom-0 right-0 w-[44%]">
        <div className="absolute inset-0 translate-x-5 translate-y-5 bg-claysoft" />
        <motion.img
          initial={reduce ? false : { opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.4, delay: 0.35, ease }}
          src={HERO_IMG}
          alt=""
          fetchPriority="high"
          data-testid="hero-image"
          className="relative h-full w-full object-cover object-[35%_20%] rounded-l-[3rem]"
        />
      </div>

      {/* A second, intimate crop overlapping the seam between type and image */}
      <motion.div
        aria-hidden="true"
        initial={reduce ? false : { opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.9, ease }}
        className="hidden lg:block absolute bottom-24 right-[39%] w-52 z-10"
      >
        <img
          src={DETAIL_IMG}
          alt=""
          loading="lazy"
          className="w-full aspect-square object-cover rounded-2xl shadow-[0_24px_50px_-20px_rgba(36,28,19,0.45)]"
        />
        <p className="mt-3 text-[10px] tracking-[0.25em] uppercase text-smoke text-right">
          <span className="bg-ivory/85 backdrop-blur-sm px-2.5 py-1.5 rounded-md inline-block">
            The details you notice at 2 a.m.
          </span>
        </p>
      </motion.div>

      <div className="relative max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20">
        <div className="lg:w-[52%] pt-32 lg:pt-0 lg:min-h-[100svh] flex flex-col justify-center">
          <motion.p
            {...rise(0.1)}
            className="text-xs tracking-[0.25em] uppercase font-semibold text-euca"
            data-testid="hero-eyebrow"
          >
            The Nurtured Parent Co. · Pediatric Sleep Consulting
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="mt-8 font-serif font-light text-ink leading-[1.08] tracking-tight text-[2.45rem] sm:text-5xl lg:text-6xl"
          >
            <motion.span {...rise(0.25)} className="block">
              Pediatric Sleep Consultant
            </motion.span>
            <motion.span {...rise(0.4)} className="block italic text-clay">
              for newborns, infants
            </motion.span>
            <motion.span {...rise(0.5)} className="block italic text-clay">
              &amp; toddlers.
            </motion.span>
          </h1>

          <motion.p
            {...rise(0.56)}
            className="mt-7 font-serif italic font-light text-clay text-2xl sm:text-3xl leading-snug"
            data-testid="hero-emotional-line"
          >
            You don&rsquo;t have to figure this out alone.
          </motion.p>

          <motion.p
            {...rise(0.62)}
            className="mt-6 max-w-md text-base sm:text-lg font-normal leading-relaxed text-ink/80"
            data-testid="hero-clarity"
          >
            Personalized support from a certified pediatric sleep consultant —
            built around your child, your family, and what&rsquo;s actually
            happening at home.
          </motion.p>

          <motion.div {...rise(0.66)} className="mt-7 flex flex-col items-start gap-4">
            <a
              data-testid="hero-book-cta"
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                trackEvent("cta_click", { location: "hero" });
                trackEvent("book_assessment_click", { location: "hero" });
              }}
              className="w-full sm:w-auto justify-center inline-flex items-center gap-3 rounded-full bg-ink text-ivory font-medium px-8 py-4 text-base hover:bg-espresso transition-colors duration-300 group"
            >
              Book my complimentary pediatric sleep assessment
              <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <p
              data-testid="hero-availability"
              className="text-[11px] tracking-[0.25em] uppercase font-semibold text-euca"
            >
              <a
                data-testid="hero-availability-virtual"
                href="/virtual-sleep-consultant"
                onClick={() => trackEvent("virtual_service_click", { location: "hero" })}
                className="hover:text-claydark transition-colors duration-300"
              >
                Virtual — Nationwide
              </a>
              &nbsp;&nbsp;·&nbsp;&nbsp;
              <a
                data-testid="hero-availability-inhome"
                href="/denver-in-home-sleep-consultant"
                onClick={() => trackEvent("in_home_service_click", { location: "hero" })}
                className="hover:text-claydark transition-colors duration-300"
              >
                In-Home/Overnight — Denver
              </a>
            </p>
            <a
              data-testid="hero-text-cta"
              href={`sms:${ANDREA_PHONE}`}
              onClick={(e) => {
                trackEvent("text_andrea_click", { location: "hero" });
                contactClick("text")(e);
              }}
              className="inline-flex items-center gap-1.5 py-2 text-sm font-normal text-smoke underline underline-offset-8 decoration-ink/20 hover:decoration-clay hover:text-ink transition-[text-decoration-color,color] duration-300"
            >
              Not ready to book? Text Andrea.
            </a>
            <p data-testid="hero-reassurance" className="text-sm font-normal leading-relaxed text-smoke max-w-sm">
              Tell Andrea what&rsquo;s happening. Ask your questions. Leave
              knowing what your next step could be.
            </p>
          </motion.div>

          <motion.div
            {...rise(0.75)}
            className="mt-8 max-w-md"
            data-testid="hero-subcopy"
          >
            <p className="text-base sm:text-lg font-normal leading-relaxed text-smoke">
              Maybe your baby falls asleep in your arms — and wakes the minute
              they touch the crib. Maybe every nap ends at 32 minutes. Maybe
              they&rsquo;re suddenly waking every 90 minutes again.
            </p>
            <p className="mt-4 text-base sm:text-lg font-normal leading-relaxed text-smoke">
              Maybe your toddler keeps coming back out of their room after
              you&rsquo;ve said goodnight. Or maybe the thing that worked
              beautifully two weeks ago suddenly doesn&rsquo;t work at all.
            </p>
            <p className="mt-6 font-serif italic font-light text-lg sm:text-xl leading-relaxed text-ink/70">
              You&rsquo;ve moved bedtime. Changed the naps. Watched the wake
              windows. Fed them back to sleep. Tried not feeding them back to
              sleep. Maybe you&rsquo;ve rocked, bounced, waited, Googled — or
              started wondering whether it&rsquo;s time for sleep training.
            </p>
            <p className="mt-6 text-sm font-normal text-smoke">
              And after all of it, you&rsquo;re still asking:
            </p>
            <p className="mt-3 font-serif italic font-light text-2xl sm:text-3xl leading-snug text-clay">
              What does my child actually need?
            </p>
          </motion.div>

          <motion.div {...rise(0.85)} className="mt-10 flex items-center gap-6">
            <button
              data-testid="hero-scroll-cue"
              onClick={() => scrollToId("sleep-help-by-age")}
              className="group inline-flex items-center gap-3 text-sm font-medium text-ink hover:text-claydark transition-colors duration-300"
            >
              <span className="w-11 h-11 rounded-full border border-ink/20 flex items-center justify-center transition-colors duration-300 group-hover:border-clay group-hover:bg-clay group-hover:text-white">
                <ArrowDown size={16} className="transition-transform duration-300 group-hover:translate-y-0.5" />
              </span>
              Start with your child
            </button>
          </motion.div>

          <motion.p {...rise(1)} className="mt-14 lg:mb-16 text-[10px] tracking-[0.3em] uppercase text-dusk">
            A quieter place to begin
          </motion.p>
        </div>

        {/* Mobile image — composed as its own spread below the statement */}
        <motion.div
          initial={reduce ? false : { opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5, ease }}
          className="lg:hidden pb-16 -mt-4"
        >
          <div className="relative">
            <div aria-hidden="true" className="absolute inset-0 translate-x-3 translate-y-3 rounded-[2rem] bg-claysoft" />
            <img
              src={HERO_IMG}
              alt="A mother holding her sleeping baby against her shoulder in warm, quiet light"
              fetchPriority="high"
              className="relative w-full aspect-[4/5] object-cover object-[35%_20%] rounded-[2rem]"
            />
          </div>
        </motion.div>

        <Thread className="text-clay/50 pb-10 hidden md:block" />
      </div>
    </section>
  );
};
